import { useState, useEffect, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import { liveAPI, objectsAPI } from '@/lib/api';
import { useLanguage } from '@/contexts/LanguageContext';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import StatusBadge from '@/components/StatusBadge';
import { formatDuration } from '@/lib/utils';
import { 
  Radio, 
  Building2, 
  Users, 
  AlertTriangle, 
  RefreshCw, 
  Loader2,
  ArrowLeft,
  Zap,
  Play,
  Pause,
  UtensilsCrossed,
  MapPinOff,
  MapPin
} from 'lucide-react';

export default function LiveView() {
  const { t } = useLanguage();
  const { objectId } = useParams();
  const [loading, setLoading] = useState(true);
  const [objects, setObjects] = useState([]);
  const [selectedObject, setSelectedObject] = useState(objectId || '');
  const [liveData, setLiveData] = useState(null);
  const [lastUpdate, setLastUpdate] = useState(null);

  const loadObjects = async () => {
    try {
      const res = await objectsAPI.list();
      setObjects(res.data);
      if (!selectedObject && res.data.length > 0) {
        setSelectedObject(res.data[0].id);
      }
    } catch (error) {
      console.error('Failed to load objects:', error);
    }
  };

  const loadLiveData = useCallback(async () => {
    if (!selectedObject) return;
    
    try {
      const res = await liveAPI.object(selectedObject);
      setLiveData(res.data);
      setLastUpdate(new Date());
    } catch (error) {
      console.error('Failed to load live data:', error);
    } finally {
      setLoading(false);
    }
  }, [selectedObject]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    loadObjects();
  }, []);

  useEffect(() => {
    if (selectedObject) {
      setLoading(true);
      loadLiveData();
      
      const interval = setInterval(loadLiveData, 30000);
      return () => clearInterval(interval);
    }
  }, [selectedObject, loadLiveData]);

  const [, setTick] = useState(0);
  useEffect(() => {
    const interval = setInterval(() => setTick(prev => prev + 1), 1000);
    return () => clearInterval(interval);
  }, []);

  const calculateDuration = (startedAt) => {
    if (!startedAt) return 0;
    const started = new Date(startedAt);
    const now = new Date();
    return Math.floor((now - started) / 1000);
  };

  if (loading && !liveData) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="relative">
          <Loader2 className="h-10 w-10 animate-spin text-indigo-500" />
          <div className="absolute inset-0 blur-xl bg-wp-medium/30 animate-pulse" />
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6 md:space-y-8" data-testid="live-view-page">
      {/* Header */}
      <div className="flex flex-col gap-4">
        <div className="flex items-start gap-3 md:gap-4">
          <Link to="/dashboard">
            <button className="p-2 rounded-xl bg-wp-dark/30 hover:bg-wp-dark/50 text-wp-light/50 hover:text-wp-lightest transition-colors border border-wp-dark/50 shrink-0">
              <ArrowLeft className="h-5 w-5" />
            </button>
          </Link>
          <div className="min-w-0">
            <h1 className="text-3xl md:text-4xl font-bold text-white font-heading flex items-center gap-3 flex-wrap">
              <div className="w-10 h-10 md:w-12 md:h-12 rounded-xl bg-wp-medium/20 flex items-center justify-center shrink-0">
                <Radio className="h-5 w-5 md:h-6 md:w-6 text-wp-light" />
              </div>
              <span className="gradient-text break-words">{t('live.title')}</span>
            </h1>
            <p className="text-wp-light/40 mt-2">{t('live.subtitle')}</p>
          </div>
        </div>

        <div className="grid grid-cols-1 md:flex md:items-center gap-3">
          {lastUpdate && (
            <div className={`flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-lg border ${
              (new Date() - lastUpdate) > 120000
                ? 'text-amber-400 bg-amber-500/10 border-amber-500/20'
                : 'text-white/30 bg-transparent border-transparent'
            }`}>
              <div className={`w-1.5 h-1.5 rounded-full ${(new Date() - lastUpdate) > 120000 ? 'bg-amber-400' : 'bg-emerald-400 animate-pulse'}`} />
              {(new Date() - lastUpdate) > 120000 ? t('live.dataStale') : t('live.live')}
            </div>
          )}
          <Select value={selectedObject} onValueChange={setSelectedObject}>
            <SelectTrigger className="w-full md:w-[250px] bg-wp-dark/30 border-wp-dark/50 text-white" data-testid="object-selector">
              <SelectValue placeholder={t('live.selectObject')} />
            </SelectTrigger>
            <SelectContent>
              {objects.map(obj => (
                <SelectItem key={obj.id} value={obj.id}>
                  <span className="flex items-center gap-2">
                    <Building2 className="h-4 w-4" />
                    {obj.name}
                  </span>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          <button 
            onClick={loadLiveData} 
            disabled={loading}
            className="w-full md:w-auto flex items-center justify-center gap-2 px-5 py-2.5 bg-wp-dark/30 hover:bg-wp-dark/50 text-wp-light/70 hover:text-wp-lightest rounded-xl border border-wp-dark/50 transition-colors disabled:opacity-50 min-h-11"
          >
            <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
            {t('live.refresh')}
          </button>
        </div>
      </div>

      {/* Summary Stats */}
      {liveData && (
        <>
          <div className="grid grid-cols-2 lg:grid-cols-6 gap-3 md:gap-4">
            {[
              { label: t('live.working'), value: liveData.total_working, icon: Play, color: 'emerald' },
              { label: t('live.onPause'), value: liveData.total_pause, icon: Pause, color: 'amber' },
              { label: t('live.atLunch'), value: liveData.total_lunch, icon: UtensilsCrossed, color: 'cyan' },
              { label: t('live.outside'), value: liveData.total_outside, icon: MapPin, color: 'slate' },
              { label: t('live.noGps'), value: liveData.total_no_gps, icon: MapPinOff, color: 'rose' },
              { label: t('live.violations'), value: liveData.total_violations, icon: AlertTriangle, color: 'rose' },
            ].map((stat) => (
              <div 
                key={stat.label} 
                className="glass-card rounded-2xl p-5 hover-lift stat-card" 
                style={{ '--accent-color': `var(--${stat.color}-500)` }}
              >
                <div className="flex items-center gap-3">
                  <div className={`w-11 h-11 rounded-xl bg-${stat.color}-500/20 flex items-center justify-center`}>
                    <stat.icon className={`h-5 w-5 text-${stat.color}-400`} />
                  </div>
                  <div>
                    <p className={`text-2xl font-bold font-heading text-${stat.color}-400`}>{stat.value}</p>
                    <p className="text-xs text-wp-light/40">{stat.label}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Employee Grid */}
          <div className="glass-card rounded-2xl overflow-hidden">
            <div className="px-4 md:px-6 py-5 border-b border-wp-dark/50 flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
              <div className="flex items-center gap-3 min-w-0">
                <div className="w-10 h-10 rounded-xl bg-wp-medium/20 flex items-center justify-center">
                  <Users className="h-5 w-5 text-wp-light" />
                </div>
                <div className="min-w-0">
                  <h2 className="text-lg font-semibold text-white font-heading break-words">
                    {t('live.employeesAt')} {liveData.object_name}
                  </h2>
                  <p className="text-sm text-wp-light/40">
                    {t('live.lastUpdated')}: {lastUpdate?.toLocaleTimeString()}
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-2 text-white/30">
                <Zap className="h-4 w-4 text-emerald-400 animate-pulse" />
                <span className="text-sm">{t('live.liveIndicator')}</span>
              </div>
            </div>
            
            <div className="p-6">
              {liveData.employees?.length === 0 ? (
                <div className="text-center py-12">
                  <Users className="h-12 w-12 mx-auto text-white/20 mb-4" />
                  <p className="text-wp-light/40">{t('live.noEmployees')}</p>
                </div>
              ) : (
                <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                  {liveData.employees?.map((emp) => {
                    const now = new Date();
                    const lastGps = emp.last_location_update ? new Date(emp.last_location_update) : null;
                    const gpsAgeMins = lastGps ? Math.floor((now - lastGps) / 60000) : null;
                    const noSignal = gpsAgeMins !== null && gpsAgeMins > 10;

                    const effectiveStatus = noSignal ? 'no_signal' : emp.status;
                    
                    const statusColors = {
                      working: 'border-emerald-500/50 bg-emerald-500/5',
                      pause: 'border-amber-500/50 bg-amber-500/5',
                      lunch: 'border-wp-light/50 bg-wp-light/5',
                      outside: 'border-slate-500/50 bg-slate-500/5',
                      no_gps: 'border-rose-500/50 bg-rose-500/5',
                      no_signal: 'border-slate-600/50 bg-slate-600/5 opacity-70'
                    };
                    const glowColors = {
                      working: 'shadow-[0_0_20px_rgba(16,185,129,0.15)]',
                      pause: 'shadow-[0_0_20px_rgba(245,158,11,0.15)]',
                      lunch: 'shadow-[0_0_20px_rgba(14,165,233,0.15)]',
                      outside: '',
                      no_gps: 'shadow-[0_0_20px_rgba(225,29,72,0.15)]',
                      no_signal: ''
                    };
                    
                    return (
                      <div
                        key={emp.user_id}
                        className={`p-5 rounded-xl border-l-4 backdrop-blur-sm transition-all hover:-translate-y-1 ${statusColors[effectiveStatus] || ''} ${glowColors[effectiveStatus] || ''} ${emp.has_violation ? 'ring-1 ring-rose-500/30' : ''}`}
                        data-testid={`employee-card-${emp.user_id}`}
                      >
                        <div className="flex items-start justify-between mb-4">
                          <div>
                            <p className="font-semibold text-white">{emp.user_name}</p>
                            <p className="text-xs text-wp-light/40">{emp.user_email}</p>
                          </div>
                          {noSignal ? (
                            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-slate-500/20 text-slate-400 border border-slate-500/30">
                              <MapPinOff className="h-3 w-3" />
                              {t('live.noSignal')}
                            </span>
                          ) : (
                            <StatusBadge status={emp.status} />
                          )}
                        </div>
                        
                        <div className="flex items-center justify-between">
                          <span className={`text-3xl font-mono font-bold ${noSignal ? 'text-white/30' : 'text-white'}`}>
                            {noSignal ? '--:--' : formatDuration(calculateDuration(emp.status_started_at))}
                          </span>
                        </div>

                        {noSignal && lastGps && (
                          <div className="mt-3 text-xs text-white/30">
                            {t('live.lastSeen')}: {gpsAgeMins >= 60 
                              ? `${Math.floor(gpsAgeMins / 60)}${t('live.hoursAgo')}` 
                              : `${gpsAgeMins}${t('live.minsAgo')}`}
                          </div>
                        )}

                        {!noSignal && emp.has_violation && (
                          <div className="mt-4 flex items-center gap-2 text-rose-400 text-xs px-3 py-2 rounded-lg bg-rose-500/10 border border-rose-500/20">
                            <AlertTriangle className="h-3.5 w-3.5" />
                            {emp.violation_reason}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
}
